/**
 * Roland Juno-106 - Synthétiseur polyphonique légendaire (1984)
 *
 * Caractéristiques:
 * - 6 voix polyphoniques
 * - 1 DCO par voix (Saw + Pulse mixables, PWM)
 * - Sub-oscillateur (square, -1 octave)
 * - Noise generator
 * - HPF non-résonant
 * - LPF 24dB résonant
 * - 1 ADSR (partagé filter/amp)
 * - LFO (vibrato, PWM, filter)
 * - Chorus légendaire (modes I, II, I+II)
 */

import * as Tone from 'tone';

export class Juno106 {
    constructor(options = {}) {
        this.performance = options.performance || { lowPower: false };
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut
        this._params = {
            // DCO
            dcoSaw: true,           // Saw waveform on/off
            dcoPulse: false,        // Pulse waveform on/off
            dcoPulseWidth: 0.5,     // Pulse width (0.1 - 0.9)
            dcoOctave: 0,           // -1, 0, +1

            // Sub oscillator
            subLevel: 0.3,

            // Noise
            noiseLevel: 0,

            // HPF (non-resonant, simple)
            hpfFreq: 0,             // 0 = off, 1-3 = frequency steps

            // LPF
            lpfCutoff: 3000,
            lpfResonance: 1,
            lpfEnvAmount: 0.5,      // 0-1
            lpfKeyTrack: 0.5,       // Keyboard tracking

            // Envelope (shared for filter and amp)
            envAttack: 0.01,
            envDecay: 0.3,
            envSustain: 0.6,
            envRelease: 0.4,

            // LFO
            lfoRate: 1,
            lfoDelay: 0,            // Delay before LFO starts
            lfoToPitch: 0,          // Vibrato amount
            lfoToFilter: 0,         // Filter modulation
            lfoToPW: 0,             // Pulse width modulation

            // Chorus (3 modes: off, I, II, I+II)
            chorusMode: 2,          // 0=off, 1=I, 2=II, 3=I+II

            // VCA mode
            vcaMode: 'env',         // 'env' or 'gate'

            // Master
            volume: 0.7
        };

        this._createSynth();
    }

    _createSynth() {
        // LFO global
        this.lfo = new Tone.LFO({
            frequency: this._params.lfoRate,
            type: 'triangle',
            min: -1,
            max: 1
        }).start();

        // LFO modulation gains
        this.lfoPitchGain = new Tone.Gain(0);
        this.lfoFilterGain = new Tone.Gain(0);
        this.lfoPWGain = new Tone.Gain(0);

        this.lfo.connect(this.lfoPitchGain);
        this.lfo.connect(this.lfoFilterGain);
        this.lfo.connect(this.lfoPWGain);

        // DCO - Saw oscillator (6 voices)
        this.sawSynth = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: 6,
            options: {
                oscillator: { type: 'sawtooth' },
                envelope: {
                    attack: this._params.envAttack,
                    decay: this._params.envDecay,
                    sustain: this._params.envSustain,
                    release: this._params.envRelease
                }
            }
        });

        // DCO - Pulse oscillator (6 voices)
        this.pulseSynth = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: 6,
            options: {
                oscillator: {
                    type: 'pulse',
                    width: this._params.dcoPulseWidth
                },
                envelope: {
                    attack: this._params.envAttack,
                    decay: this._params.envDecay,
                    sustain: this._params.envSustain,
                    release: this._params.envRelease
                }
            }
        });

        // Sub oscillator (square, -1 octave)
        this.subSynth = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: 6,
            options: {
                oscillator: { type: 'square' },
                envelope: {
                    attack: this._params.envAttack,
                    decay: this._params.envDecay,
                    sustain: this._params.envSustain,
                    release: this._params.envRelease
                }
            }
        });

        // Noise
        this.noise = new Tone.Noise('white');
        this.noiseGain = new Tone.Gain(0);
        this.noiseEnv = new Tone.AmplitudeEnvelope({
            attack: this._params.envAttack,
            decay: this._params.envDecay,
            sustain: this._params.envSustain,
            release: this._params.envRelease
        });

        // Mixer gains
        this.sawGain = new Tone.Gain(this._params.dcoSaw ? 0.5 : 0);
        this.pulseGain = new Tone.Gain(this._params.dcoPulse ? 0.5 : 0);
        this.subGain = new Tone.Gain(this._params.subLevel);

        // HPF (simple high-pass)
        this.hpf = new Tone.Filter({
            frequency: 20,
            type: 'highpass',
            rolloff: -12
        });

        // LPF (24dB ladder-style)
        this.lpf = new Tone.Filter({
            frequency: this._params.lpfCutoff,
            type: 'lowpass',
            rolloff: -24,
            Q: this._params.lpfResonance
        });

        // Filter envelope
        this.filterEnv = new Tone.Envelope({
            attack: this._params.envAttack,
            decay: this._params.envDecay,
            sustain: this._params.envSustain,
            release: this._params.envRelease
        });

        this.filterEnvGain = new Tone.Gain(this._params.lpfEnvAmount * 5000);
        this.filterEnv.connect(this.filterEnvGain);
        this.filterEnvGain.connect(this.lpf.frequency);

        // LFO to filter
        this.lfoFilterGain.connect(this.lpf.frequency);

        // Chorus - Le son signature du Juno!
        // Mode I: slow/shallow, Mode II: fast/deep, I+II: both
        this.chorusI = new Tone.Chorus({
            frequency: 0.5,
            delayTime: 4,
            depth: 0.3,
            wet: 0
        }).start();

        this.chorusII = new Tone.Chorus({
            frequency: 0.8,
            delayTime: 3,
            depth: 0.5,
            wet: 0
        }).start();

        // Output
        this.outputGain = new Tone.Gain(this._params.volume);

        // Audio chain
        this.sawSynth.connect(this.sawGain);
        this.pulseSynth.connect(this.pulseGain);
        this.subSynth.connect(this.subGain);

        this.noise.connect(this.noiseEnv);
        this.noiseEnv.connect(this.noiseGain);

        // All sources -> HPF -> LPF
        this.sawGain.connect(this.hpf);
        this.pulseGain.connect(this.hpf);
        this.subGain.connect(this.hpf);
        this.noiseGain.connect(this.hpf);

        this.hpf.connect(this.lpf);

        // LPF -> Chorus chain -> Output
        this.lpf.connect(this.chorusI);
        this.chorusI.connect(this.chorusII);
        this.chorusII.connect(this.outputGain);

        // Set initial chorus mode
        this._updateChorusMode();

        // Start noise
        this._startOscillators();
    }

    _startOscillators() {
        this.noise.start();
    }

    _stopOscillators() {
        this.noise.stop();
    }

    _updateChorusMode() {
        switch (this._params.chorusMode) {
            case 0: // Off
                this.chorusI.wet.value = 0;
                this.chorusII.wet.value = 0;
                break;
            case 1: // I only
                this.chorusI.wet.value = 0.5;
                this.chorusII.wet.value = 0;
                break;
            case 2: // II only
                this.chorusI.wet.value = 0;
                this.chorusII.wet.value = 0.5;
                break;
            case 3: // I + II
                this.chorusI.wet.value = 0.4;
                this.chorusII.wet.value = 0.4;
                break;
        }
    }

    _getHpfFrequency(step) {
        // HPF has 4 positions: off, low, mid, high
        const freqs = [20, 100, 250, 500];
        return freqs[Math.min(step, 3)];
    }

    connectTo(destination) {
        this.mixerChannel = destination;
        if (this.isPowered) {
            this.outputGain.connect(destination);
        }
    }

    setPower(on) {
        this.isPowered = on;
        if (this.mixerChannel) {
            if (on) {
                this.outputGain.connect(this.mixerChannel);
                if (this.performance.lowPower) {
                    this._startOscillators();
                }
            } else {
                this.outputGain.disconnect();
                this.sawSynth.releaseAll();
                this.pulseSynth.releaseAll();
                this.subSynth.releaseAll();
                if (this.performance.lowPower) {
                    this._stopOscillators();
                }
            }
        }
    }

    getPower() {
        return this.isPowered;
    }

    triggerAttack(note, time, velocity = 1) {
        if (!this.isPowered) return;

        const notes = Array.isArray(note) ? note : [note];
        const vel = velocity * this._params.volume;

        // Transpose notes based on octave setting
        const dcoNotes = notes.map(n => this._transposeNote(n, this._params.dcoOctave * 12));
        const subNotes = notes.map(n => this._transposeNote(n, (this._params.dcoOctave - 1) * 12));

        if (this._params.dcoSaw) {
            this.sawSynth.triggerAttack(dcoNotes, time, vel * 0.5);
        }
        if (this._params.dcoPulse) {
            this.pulseSynth.triggerAttack(dcoNotes, time, vel * 0.5);
        }
        if (this._params.subLevel > 0) {
            this.subSynth.triggerAttack(subNotes, time, vel * this._params.subLevel);
        }

        this.filterEnv.triggerAttack(time);

        if (this._params.noiseLevel > 0) {
            this.noiseEnv.triggerAttack(time);
        }
    }

    triggerRelease(note, time) {
        if (!this.isPowered) return;

        const notes = Array.isArray(note) ? note : [note];
        const dcoNotes = notes.map(n => this._transposeNote(n, this._params.dcoOctave * 12));
        const subNotes = notes.map(n => this._transposeNote(n, (this._params.dcoOctave - 1) * 12));

        this.sawSynth.triggerRelease(dcoNotes, time);
        this.pulseSynth.triggerRelease(dcoNotes, time);
        this.subSynth.triggerRelease(subNotes, time);

        this.filterEnv.triggerRelease(time);
        this.noiseEnv.triggerRelease(time);
    }

    triggerAttackRelease(note, duration, time, velocity = 1) {
        this.triggerAttack(note, time, velocity);
        this.triggerRelease(note, time + Tone.Time(duration).toSeconds());
    }

    _transposeNote(note, semitones) {
        const freq = Tone.Frequency(note).toFrequency();
        return freq * Math.pow(2, semitones / 12);
    }

    // === Setters ===

    setDcoSaw(on) {
        this._params.dcoSaw = on;
        this.sawGain.gain.value = on ? 0.5 : 0;
    }

    setDcoPulse(on) {
        this._params.dcoPulse = on;
        this.pulseGain.gain.value = on ? 0.5 : 0;
    }

    setDcoPulseWidth(width) {
        this._params.dcoPulseWidth = width;
        this.pulseSynth.set({ oscillator: { width: width } });
    }

    setDcoOctave(oct) {
        this._params.dcoOctave = oct;
    }

    setSubLevel(level) {
        this._params.subLevel = level;
        this.subGain.gain.value = level;
    }

    setNoiseLevel(level) {
        this._params.noiseLevel = level;
        this.noiseGain.gain.value = level;
    }

    setHpfFreq(step) {
        this._params.hpfFreq = step;
        this.hpf.frequency.value = this._getHpfFrequency(step);
    }

    setLpfCutoff(freq) {
        this._params.lpfCutoff = freq;
        this.lpf.frequency.value = freq;
    }

    setLpfResonance(q) {
        this._params.lpfResonance = q;
        this.lpf.Q.value = q;
    }

    setLpfEnvAmount(amount) {
        this._params.lpfEnvAmount = amount;
        this.filterEnvGain.gain.value = amount * 5000;
    }

    setLpfKeyTrack(amount) {
        this._params.lpfKeyTrack = amount;
    }

    setEnvAttack(time) {
        this._params.envAttack = time;
        this.sawSynth.set({ envelope: { attack: time } });
        this.pulseSynth.set({ envelope: { attack: time } });
        this.subSynth.set({ envelope: { attack: time } });
        this.filterEnv.attack = time;
        this.noiseEnv.attack = time;
    }

    setEnvDecay(time) {
        this._params.envDecay = time;
        this.sawSynth.set({ envelope: { decay: time } });
        this.pulseSynth.set({ envelope: { decay: time } });
        this.subSynth.set({ envelope: { decay: time } });
        this.filterEnv.decay = time;
        this.noiseEnv.decay = time;
    }

    setEnvSustain(level) {
        this._params.envSustain = level;
        this.sawSynth.set({ envelope: { sustain: level } });
        this.pulseSynth.set({ envelope: { sustain: level } });
        this.subSynth.set({ envelope: { sustain: level } });
        this.filterEnv.sustain = level;
        this.noiseEnv.sustain = level;
    }

    setEnvRelease(time) {
        this._params.envRelease = time;
        this.sawSynth.set({ envelope: { release: time } });
        this.pulseSynth.set({ envelope: { release: time } });
        this.subSynth.set({ envelope: { release: time } });
        this.filterEnv.release = time;
        this.noiseEnv.release = time;
    }

    setLfoRate(rate) {
        this._params.lfoRate = rate;
        this.lfo.frequency.value = rate;
    }

    setLfoDelay(delay) {
        this._params.lfoDelay = delay;
    }

    setLfoToPitch(amount) {
        this._params.lfoToPitch = amount;
        this.lfoPitchGain.gain.value = amount * 100;
    }

    setLfoToFilter(amount) {
        this._params.lfoToFilter = amount;
        this.lfoFilterGain.gain.value = amount * 2000;
    }

    setLfoToPW(amount) {
        this._params.lfoToPW = amount;
        this.lfoPWGain.gain.value = amount * 0.4;
    }

    setChorusMode(mode) {
        this._params.chorusMode = mode;
        this._updateChorusMode();
    }

    setVcaMode(mode) {
        this._params.vcaMode = mode;
    }

    setVolume(vol) {
        this._params.volume = vol;
        this.outputGain.gain.value = vol;
    }

    getParams() {
        return { ...this._params };
    }

    setParams(params) {
        Object.entries(params).forEach(([key, value]) => {
            const setter = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
            if (typeof this[setter] === 'function') {
                this[setter](value);
            }
        });
    }

    dispose() {
        this.sawSynth.dispose();
        this.pulseSynth.dispose();
        this.subSynth.dispose();
        this.noise.dispose();
        this.noiseGain.dispose();
        this.noiseEnv.dispose();
        this.sawGain.dispose();
        this.pulseGain.dispose();
        this.subGain.dispose();
        this.hpf.dispose();
        this.lpf.dispose();
        this.filterEnv.dispose();
        this.filterEnvGain.dispose();
        this.chorusI.dispose();
        this.chorusII.dispose();
        this.outputGain.dispose();
        this.lfo.dispose();
        this.lfoPitchGain.dispose();
        this.lfoFilterGain.dispose();
        this.lfoPWGain.dispose();
    }
}
